import { useCallback, useEffect, useRef, useState } from 'react'
import { com, kotlin } from '@eidu/web'
import FormContentInput from '../../../../components/input/FormContentInput'
import { safeDecodeFormContentYaml, DecodedFormContent } from './formContentYaml'
import { useFormErrors } from '../../../../util/FormErrorBus'
import KtList = kotlin.collections.KtList
import Page = com.eidu.sharedlib.form.definition.Page
import encodeFormContentToYaml = com.eidu.sharedlib.form.encodeFormContentToYaml

type FormContentInputAdapterProps = {
  yaml: string
  editable: boolean
  onYamlChange?: (yaml: string) => void
}

/**
 * Bridges the asset edit page's YAML string payload to the page-based FormContentInput. We hold the decoded pages in
 * local state (rather than decoding on every render) so the editor keeps the per-page identity it relies on for
 * expansion/focus; we only re-decode when the YAML changes from outside our own echo.
 */
const FormContentInputAdapter = ({ yaml, editable, onYamlChange }: FormContentInputAdapterProps) => {
  const [decoded, setDecoded] = useState<DecodedFormContent>(() => safeDecodeFormContentYaml(yaml))
  const { pages, error: decodeError } = decoded
  const lastSyncedYaml = useRef<string>(yaml)
  const { clearAll } = useFormErrors()

  // Live field errors are owned by the widgets inside this editor; clear them on unmount so a stale error can't
  // wedge the Save button after switching to YAML mode, where these widgets no longer exist (see ADR-0001).
  useEffect(() => () => clearAll(), [clearAll])

  useEffect(() => {
    if (yaml === lastSyncedYaml.current) return
    setDecoded(safeDecodeFormContentYaml(yaml))
    lastSyncedYaml.current = yaml
  }, [yaml])

  const handleChange = useCallback(
    (newPages: KtList<Page>) => {
      setDecoded((prev) => ({ pages: newPages, error: prev.error }))
      const encoded = encodeFormContentToYaml(newPages)
      lastSyncedYaml.current = encoded
      onYamlChange?.(encoded)
    },
    [onYamlChange]
  )

  return <FormContentInput pages={pages} onChange={editable ? handleChange : undefined} errorMessage={decodeError} />
}

export default FormContentInputAdapter
